import { getSupportedFormatsText } from '../config/fileUpload';

interface PendingFile {
  id: string;
  file: File;
  status: 'pending' | 'uploading' | 'uploaded' | 'error';
  progress?: number;
  error?: string;
}

interface FilePreviewProps {
  files: PendingFile[];
  onRemove: (id: string) => void;
}

// Trash icon for delete
const TrashIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

function getFileIcon(type: string): string {
  if (type.startsWith('image/')) return '🖼️';
  if (type.includes('pdf')) return '📄';
  if (type.includes('spreadsheet') || type.includes('excel') || type.includes('csv')) return '📊';
  if (type.includes('dwg') || type.includes('dxf') || type.includes('svg')) return '📐';
  return '📎';
}


export function FilePreview({ files, onRemove }: FilePreviewProps) {
  if (files.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5">
      {files.map((pf) => (
        <div
          key={pf.id}
          className={`
            flex items-center gap-1.5 px-2 py-1 rounded text-xs
            ${pf.status === 'error' ? 'bg-red-50' : 'bg-gray-100'}
          `}
        >
          <span className="text-sm">{getFileIcon(pf.file.type)}</span>
          <span className="font-medium truncate max-w-[100px]">
            {pf.file.name}
          </span>

          {pf.status === 'uploading' && pf.progress !== undefined && (
            <span className="text-blue-600 text-[10px]">{pf.progress}%</span>
          )}

          {pf.status === 'uploaded' && (
            <span className="text-green-600 text-[10px]">✓</span>
          )}

          {pf.status === 'error' && (
            <span
              className="text-red-600 text-[10px] cursor-help"
              title={`${pf.error}\n\nSupported: ${getSupportedFormatsText()}`}
            >
              ⚠
            </span>
          )}

          <button
            onClick={() => onRemove(pf.id)}
            className="p-0.5 text-gray-400 hover:text-red-500 rounded"
            aria-label={`Remove ${pf.file.name}`}
            title="Remove file"
          >
            <TrashIcon />
          </button>
        </div>
      ))}
    </div>
  );
}

export type { PendingFile };
